import { Plugin, PluginKey, TextSelection, Selection } from 'prosemirror-state'
import { schema } from 'prose-core'

import { Decoration, DecorationSet, EditorView } from 'prosemirror-view'
import styles from './PredictAI.module.scss'
import { predictAI, PredictCommandMode } from 'common/Prose/Commands/predictAI'
// import { isMacintosh } from 'utils'

export const PredictAIPluginKey = new PluginKey('PredictAI')

export enum PredictAIStatus {
  'idle' = 'idle',
  'predicting' = 'predicting',
  'done' = 'done',
}

const getDeco = (pluginState: PredictAIPluginState) => (view: EditorView) => {
  // const cmd = isMacintosh() ? '⌘' : 'ctrl'
  const el = document.createElement('span')
  el.classList.add(styles.prediction, styles[pluginState.status])
  if (pluginState.status === PredictAIStatus.predicting) {
    el.innerHTML = `<span class="${styles.fakeAssistantCursor}"><span class="${styles.label}">Wiz</span></span>`
  }

  return el
}

const isValidPositionForPrediction = (selection: Selection) => {
  return (
    selection instanceof TextSelection &&
    selection.empty &&
    selection.$anchor.parent.type === schema.nodes.paragraph &&
    selection.$anchor.parentOffset + 2 >= selection.$anchor.parent.nodeSize
  )
}

export type PredictAIPluginState = { position: number; timeoutID?: NodeJS.Timeout; intervalID?: NodeJS.Timer } & (
  | { status: PredictAIStatus.idle }
  | { status: PredictAIStatus.predicting; chunks: Array<string | null> }
  | {
      status: PredictAIStatus.done
    }
)
export type PredictAIPluginMeta =
  | {
      action: 'start'
      position: number
    }
  | {
      action: 'chunkReceived'
      chunk: Array<string | null>
    }
  | {
      action: 'chunkProcessed'
      chunks: number
    }
  | {
      action: 'done'
    }

export const PredictAIPlugin = new Plugin({
  key: PredictAIPluginKey,
  state: {
    init: (): PredictAIPluginState => ({
      status: PredictAIStatus.idle,
      position: 0,
    }),
    apply: (tr, pluginState: PredictAIPluginState, oldState): PredictAIPluginState => {
      const newPosition = tr.mapping.map(pluginState.position)
      const pluginMeta: PredictAIPluginMeta = tr.getMeta(PredictAIPluginKey)

      if (pluginMeta) {
        switch (pluginMeta.action) {
          case 'start': {
            const updateTimer = setInterval(() => {
              document.dispatchEvent(new CustomEvent('updatePrediction'))
            }, 20)
            return { status: PredictAIStatus.predicting, position: newPosition, chunks: [], intervalID: updateTimer }
          }
          case 'chunkReceived': {
            if (pluginState.status !== PredictAIStatus.predicting) {
              return pluginState
            }
            return {
              ...pluginState,
              position: newPosition,
              chunks: [...pluginState.chunks, ...pluginMeta.chunk],
            }
          }
          case 'chunkProcessed': {
            if (pluginState.status !== PredictAIStatus.predicting) {
              return pluginState
            }
            return {
              ...pluginState,
              position: newPosition,
              chunks: pluginState.chunks.slice(pluginMeta.chunks),
            }
          }
          case 'done': {
            clearInterval(pluginState.intervalID)
            return { status: PredictAIStatus.done, position: newPosition }
          }
        }
      } else {
        if (
          pluginState.status !== PredictAIStatus.idle &&
          !tr.docChanged &&
          tr.selection.anchor !== oldState.selection.anchor
        ) {
          // the user moved the caret, so we should stop predicting
          clearInterval(pluginState.intervalID)
          return { status: PredictAIStatus.idle, position: tr.selection.anchor }
        }
      }

      if (pluginState.status === PredictAIStatus.idle && isValidPositionForPrediction(tr.selection)) {
        // we should show the widget
        return { status: PredictAIStatus.idle, position: tr.selection.anchor }
      }

      return { ...pluginState, position: newPosition }
    },
  },
  view: (editorView) => {
    const handleStartPrediction = () => {
      predictAI(PredictCommandMode.automatic)(editorView.state, editorView.dispatch)
    }
    const handleChunkReceived = (e: CustomEvent<Array<string | null>>) => {
      const pluginState: PredictAIPluginState = PredictAIPluginKey.getState(editorView.state)
      if (pluginState.status === PredictAIStatus.predicting) {
        const pluginMeta: PredictAIPluginMeta = { action: 'chunkReceived', chunk: e.detail }
        editorView.dispatch(editorView.state.tr.setMeta(PredictAIPluginKey, pluginMeta))
      }
    }
    const handleUpdatePrediction = () => {
      const pluginState: PredictAIPluginState = PredictAIPluginKey.getState(editorView.state)
      if (pluginState.status === PredictAIStatus.predicting && pluginState.chunks.length > 0) {
        const chunksWithoutDone = pluginState.chunks.filter((chunk) => chunk !== null)
        const {
          state: { tr },
          dispatch,
        } = editorView
        if (chunksWithoutDone.length > 0) {
          const pluginMeta: PredictAIPluginMeta = {
            action: 'chunkProcessed',
            chunks: chunksWithoutDone.length,
          }
          tr.insertText(chunksWithoutDone.join(''))
          tr.setMeta(PredictAIPluginKey, pluginMeta)
        } else if (pluginState.chunks.length === 1 && pluginState.chunks[0] === null) {
          const pluginMeta: PredictAIPluginMeta = {
            action: 'done',
          }
          tr.setMeta(PredictAIPluginKey, pluginMeta)
        }
        dispatch(tr)
      }
    }
    document.addEventListener('startPrediction', handleStartPrediction)
    document.addEventListener('updatePrediction', handleUpdatePrediction as any)
    document.addEventListener('chunkReceived', handleChunkReceived as any)

    return {
      destroy() {
        document.removeEventListener('startPrediction', handleStartPrediction)
        document.removeEventListener('updatePrediction', handleUpdatePrediction as any)
        document.removeEventListener('chunkReceived', handleChunkReceived as any)
        const pluginState: PredictAIPluginState = PredictAIPluginKey.getState(editorView.state)
        if (pluginState.timeoutID) {
          clearTimeout(pluginState.timeoutID)
        }
      },
    }
  },
  props: {
    decorations: (state) => {
      const { selection } = state
      const pluginState = PredictAIPluginKey.getState(state) as PredictAIPluginState

      if (pluginState.status === PredictAIStatus.predicting) {
        const pos = selection.anchor
        return DecorationSet.create(state.doc, [
          Decoration.widget(pos, getDeco(pluginState), { key: `predict-${pluginState.status}` }),
        ])
      }

      return DecorationSet.empty
    },
  },
})
